import { getAllEnumValues } from '@upright/common/utils';
import { Dictionary, max } from 'lodash';
import { AccessLevel } from '../../iam/access/types';
import { EuNfrdAssessmentType, EuTaxonomyAssessmentType, TnfaAssessmentType } from './eu-taxonomy/types';
import { MeasurableGroupType } from './measurable-group/types';
import { PaiMetric } from './pai-assessments/types';
import { z } from 'zod';

export enum ModelEntityType {
  PRODUCT = 'product',
  MIXIN = 'mixin',
  PHENOMENON = 'phenomenon',
  COMPANY = 'company',
  GROUP = 'group',
  PORTFOLIO = 'portfolio',
}
const zModelEntityType = z.nativeEnum(ModelEntityType);

export const allModelEntityTypes = getAllEnumValues(ModelEntityType, 'string');

export type ProductLikeEntityType = ModelEntityType.PRODUCT | ModelEntityType.MIXIN | ModelEntityType.PHENOMENON;
export type CompanyLikeEntityType = ModelEntityType.COMPANY | ModelEntityType.GROUP | ModelEntityType.PORTFOLIO;

export const productLikeEntityTypes = [
  ModelEntityType.PRODUCT,
  ModelEntityType.MIXIN,
  ModelEntityType.PHENOMENON,
] as const;

export const companyLikeEntityTypes = [
  ModelEntityType.COMPANY,
  ModelEntityType.GROUP,
  ModelEntityType.PORTFOLIO,
] as const;

export const isCompanyLikeEntityType = (entityType: ModelEntityType | undefined) => {
  return companyLikeEntityTypes.includes(entityType as CompanyLikeEntityType);
};

export enum EntityIdentifierType {
  EID = 'EID',

  // Non-EID not migrated yet, but kept here because merge logic already supports them:
  ISIN = 'ISIN',
  LEI = 'LEI',
  VATIN = 'VATIN',

  // Included for blacklist only:
  name = 'name',
  website = 'website',
  ticker = 'ticker',
  issuerName = 'issuerName',
}

export const searchableModelEntityTypes: ModelEntityType[] = [
  ModelEntityType.PRODUCT,
  ModelEntityType.PHENOMENON,
  ModelEntityType.MIXIN,
  ModelEntityType.COMPANY,
  ModelEntityType.GROUP,
  ModelEntityType.PORTFOLIO,
];

export const zModelEntityReference = z.object({
  id: z.string(),
  type: zModelEntityType,
});
export type ModelEntityReference = z.infer<typeof zModelEntityReference>;

export type ModelEntityReferenceWithEntityId = ModelEntityReference & { entityId?: string };

export interface NamedModelEntityReference {
  id: string;
  type: ModelEntityType;
  name: string;
  itemName?: string;
  itemTicker?: string;
}
export interface NamedModelEntityReferenceWithAccessLevel extends NamedModelEntityReference {
  accessLevel?: AccessLevel;
}

export interface PossiblyNamedModelEntityReference {
  id: string;
  type: ModelEntityType;
  name?: string;
}

export interface NamedModelEntityReferenceWithGroupType extends NamedModelEntityReference {
  groupOrPortfolioType: MeasurableGroupType;
}

export interface PossiblyNamedModelEntityReferenceWithGroupType extends PossiblyNamedModelEntityReference {
  groupOrPortfolioType?: MeasurableGroupType;
}

export interface PossiblyNamedModelEntityReferenceWithAccessLevel extends PossiblyNamedModelEntityReference {
  id: string;
  type: ModelEntityType;
  name?: string;
  itemName?: string;
  itemTicker?: string;
  accessLevel?: AccessLevel;
  itemCreated?: string;
  legalEntityId?: string; // Only significant for companies in weight sets. Also filled out for groupfolios with values identical to entityName and entityId for compatibility reasons. Not significant for products.
  legalEntityName?: string; // --//--
  entityName?: string; // --//--
  entityId?: string; // --//--
}

export interface PossiblyNamedModelEntityReferenceWithAccessLevelAndGroupType extends PossiblyNamedModelEntityReferenceWithAccessLevel {
  groupType?: MeasurableGroupType;
}

export interface WeightableModelEntityRef extends Partial<PossiblyNamedModelEntityReferenceWithAccessLevelAndGroupType> {
  // TODO improve typing, we may not populate (or need) accessLevel and groupType
  name?: string; // We should always have this, but keep it optional for compatibility with PossiblyNamed...
  itemId: number;
}

export interface NamedModelEntityReferenceWithAccessLevelAndGroupType extends NamedModelEntityReferenceWithAccessLevel {
  groupType?: MeasurableGroupType;
}

export interface Weightable {
  revenue: number | undefined;
  weight: number | undefined;
  modelled: boolean; // ie. linked to an entitity that has scores
}

interface WithValue {
  value: number | undefined;
  valueCurrency: string | undefined;
}

interface WithItemId {
  itemId: number;
}

export type WeightablePossiblyNamedModelEntityReference = WithItemId &
  Weightable &
  WithValue &
  PossiblyNamedModelEntityReferenceWithAccessLevel;

export interface NamedModelEntityReferenceWithSource {
  id: string;
  type: ModelEntityType;
  name: string;
  source: string;
}

// OA: This is functionally identical to Dictionary<T>, but I find the explicit naming
// helpful when visualizing the shape of data.
export interface ByEntityId<T> {
  [entityId: string]: T;
}

// Note: localization is only used for the API /metrics/legend and /profile endpoints (ie. desktop render view)
export enum ImpactLanguage {
  en = 'en',
  fi = 'fi',
  sv = 'sv',
}

export type ByLanguage<T> = {
  [lang in ImpactLanguage]: T;
};

export const allImpactLanguages = Object.values(ImpactLanguage);

// Note: higher values are assumed less trustworthy in combineMetricNatures
export enum MetricNature {
  DIRECT_DISCLOSURE = 1,
  THIRD_PARTY_ASSESSMENT = 2,
  UPRIGHT_MANUAL_ASSESSMENT = 3, // NOTE! presented externally as Upright modelled estimate!
  UPRIGHT_ESTIMATE = 4, // NOTE! Referred to in upright-sagemaker-inference-api
}

export function combineMetricNatures(natures: MetricNature[]) {
  const selected = max(natures);
  if (selected === undefined) {
    throw Error('Cannot combine an empty array of natures');
  }
  return selected;
}

/*
 * Present Upright manual assessments externally as Upright modelled estimates
 */
export function isUprightModelledExternal(nature: MetricNature) {
  return isUprightModelledInternal(nature) || nature === MetricNature.UPRIGHT_MANUAL_ASSESSMENT;
}

/*
 * Internally in compute, recognize only machine generated estimates as estimates
 */
export function isUprightModelledInternal(nature: MetricNature) {
  return nature === MetricNature.UPRIGHT_ESTIMATE;
}

// List of natures that can be entered by humans
export const assessmentNatures = getAllEnumValues(MetricNature, 'number').filter(
  nature => !isUprightModelledInternal(nature),
);

export function toExternalNature(nature: MetricNature) {
  if (isUprightModelledExternal(nature)) {
    return MetricNature.UPRIGHT_ESTIMATE;
  }
  return nature;
}

export enum AssessmentPrioritization {
  SELECTED = 0,
  INGORED = 1,
  OTHER = 2,
}

export interface AndAssessmentPriorization {
  state: AssessmentPrioritization;
}

export function friendlyAssessmentNatureName(nature: MetricNature) {
  switch (nature) {
    case MetricNature.DIRECT_DISCLOSURE:
      return 'Direct company disclosure';
    case MetricNature.THIRD_PARTY_ASSESSMENT:
      return 'Upright public disclosure database';
    case MetricNature.UPRIGHT_MANUAL_ASSESSMENT:
      return 'Upright modelled estimate';
    case MetricNature.UPRIGHT_ESTIMATE:
      return 'Upright modelled estimate';
  }
}

// NOTE: keep in sync with joiBaseCompanyAssessment
export interface IBaseCompanyAssessment {
  id?: number;
  companyId: string;
  metric: EuNfrdAssessmentType | EuTaxonomyAssessmentType | TnfaAssessmentType | PaiMetric;
  nature: MetricNature;
  isInactive: boolean;
  year: number;
  value: number | null;
  rationale?: string;
  trace?: string; // Microwork / label studio task identifier, if any
  source?: string; // Some data source idenifier, eg. 'data-source/company-name/66037'
  sourceUrl?: string; // The original download URL of the PDF
  pdfUrl?: string; // The S3 URL of the full PDF
  pageUrl?: string; // The S3 URL of the PDF page
  createdBy?: number;
  readableBy: number;
}

export type PartialDict<T> = Partial<Dictionary<T>>;

export type Mutable<Type> = {
  -readonly [Key in keyof Type]: Type[Key];
};
